import { Send, Mail, Bell, ExternalLink } from 'lucide-react';
import type { BroadcastButton } from '../types';

// ─── Telegram Preview ────────────────────────────────────────────────────────

export function TelegramPreview({
  message,
  mediaUrl,
  buttons,
}: {
  message: string;
  mediaUrl?: string;
  buttons?: BroadcastButton[];
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Send className="h-4 w-4" />
        Telegram
      </div>
      <div className="bg-[#e8f5e9] dark:bg-[#1b3a26] rounded-xl overflow-hidden max-w-md">
        {/* Медиа */}
        {mediaUrl && (
          <img
            src={mediaUrl}
            alt="Медиа-вложение"
            className="w-full max-h-48 object-cover"
          />
        )}
        <div className="px-3 pt-2 pb-1">
          <span className="text-xs font-semibold text-[#2e7d32] dark:text-[#81c784]">
            Бот рассылки
          </span>
        </div>
        <div className="px-3 pb-3 whitespace-pre-wrap text-sm">
          {message || <span className="text-muted-foreground italic">Текст сообщения пуст</span>}
        </div>
        {/* Кнопки */}
        {buttons && buttons.length > 0 && (
          <div className="border-t border-[#c8e6c9] dark:border-[#2e5e3e] px-2 pb-2 pt-1.5 space-y-1">
            {buttons.map((btn) => (
              <div
                key={`tg-${btn.text}`}
                className="flex items-center justify-center gap-1 rounded-md bg-white/60 dark:bg-white/10 px-3 py-1.5 text-sm text-[#2196F3] dark:text-[#64b5f6] font-medium"
              >
                {btn.text}
                {btn.url && <ExternalLink className="h-3 w-3" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Email Preview ───────────────────────────────────────────────────────────

export function EmailPreview({
  message,
  buttons,
  html,
}: {
  message: string;
  buttons?: BroadcastButton[];
  html?: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Mail className="h-4 w-4" />
        Email
      </div>
      <div className="max-w-md border rounded-lg overflow-hidden bg-white dark:bg-zinc-900">
        {/* Email header */}
        <div className="px-6 py-4 border-b bg-muted/30">
          <p className="text-xs text-muted-foreground">От: Masters Club &lt;noreply@masters-club.com&gt;</p>
          <p className="text-sm font-medium mt-1">Рассылка</p>
        </div>
        {/* Email body */}
        {html && html.trim().length > 0 ? (
          <div
            className="email-preview-html"
            // Превью визуального письма: HTML собран нашим редактором, не пользовательский ввод
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="whitespace-pre-wrap text-sm leading-relaxed">
              {message || <span className="text-muted-foreground italic">Текст письма пуст</span>}
            </div>
            {/* CTA кнопки */}
            {buttons && buttons.length > 0 && (
              <div className="space-y-2 pt-2">
                {buttons.map((btn) => (
                  <div
                    key={`email-${btn.text}`}
                    className="block w-full text-center px-6 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-medium"
                  >
                    {btn.text}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Email footer */}
        <div className="px-6 py-3 border-t bg-muted/20">
          <p className="text-xs text-muted-foreground text-center">
            Masters Club
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── App Preview ─────────────────────────────────────────────────────────────

export function AppPreview({
  message,
  buttons,
}: {
  message: string;
  buttons?: BroadcastButton[];
}) {
  const truncatedMessage = message.length > 120
    ? `${message.slice(0, 120)}...`
    : message;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Bell className="h-4 w-4" />
        In-App уведомление
      </div>
      <div className="max-w-md border rounded-xl overflow-hidden bg-white dark:bg-zinc-900 shadow-sm">
        <div className="px-4 py-3 space-y-2">
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Bell className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Masters Club</p>
              <p className="text-xs text-muted-foreground">сейчас</p>
            </div>
          </div>
          {/* Message */}
          <p className="text-sm whitespace-pre-wrap">
            {truncatedMessage || <span className="text-muted-foreground italic">Текст уведомления пуст</span>}
          </p>
          {/* Кнопки */}
          {buttons && buttons.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {buttons.map((btn) => (
                <div
                  key={`app-${btn.text}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-medium text-primary"
                >
                  {btn.text}
                  {btn.url && <ExternalLink className="h-3 w-3" />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
